import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Send, Trash2, MessageCircleHeart } from "lucide-react";
import { toast } from "sonner";

const Companion = () => {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const navigate = useNavigate();

  const loadSessions = async () => {
    const { data } = await api.get("/chat/sessions");
    setSessions(data);
    return data;
  };

  useEffect(() => {
    (async () => {
      const list = await loadSessions();
      if (list.length) selectSession(list[0].id);
      else await newSession();
    })();
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const newSession = async () => {
    const { data } = await api.post("/chat/sessions", { title: "New conversation" });
    await loadSessions();
    setActive(data.id);
    setMessages([]);
  };

  const selectSession = async (id) => {
    setActive(id);
    const { data } = await api.get(`/chat/sessions/${id}/messages`);
    setMessages(data);
  };

  const deleteSession = async (id) => {
    await api.delete(`/chat/sessions/${id}`);
    const list = await loadSessions();
    if (id === active) { list.length ? selectSession(list[0].id) : newSession(); }
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { id: "tmp", role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post(`/chat/sessions/${active}/messages`, { content: text });
      setMessages((m) => [...m.filter((x) => x.id !== "tmp"), { id: "u" + Date.now(), role: "user", content: text }, data]);
      loadSessions();
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== "tmp"));
      if (err.response?.status === 402) {
        toast.error(err.response.data?.detail || "You've reached today's free limit.", {
          action: { label: "Upgrade", onClick: () => navigate("/app/plus") },
          duration: 8000,
        });
      } else {
        toast.error("Aurea couldn't respond just now. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-10rem)]" data-testid="companion-page">
      <div className="lg:w-64 shrink-0 flex flex-col">
        <Button onClick={newSession} className="rounded-full bg-brand hover:bg-brand-hover text-linen mb-4" data-testid="new-session">
          <Plus className="w-4 h-4 mr-1" /> New conversation
        </Button>
        <div className="flex-1 overflow-y-auto space-y-2">
          {sessions.map((sn) => (
            <div key={sn.id} className={`group flex items-center gap-2 rounded-2xl px-3 py-2.5 cursor-pointer transition-colors ${active === sn.id ? "bg-brand text-linen" : "bg-white border border-linen-3 text-ink-2 hover:bg-linen-2"}`} onClick={() => selectSession(sn.id)} data-testid="session-item">
              <MessageCircleHeart className="w-4 h-4 shrink-0" />
              <span className="text-sm truncate flex-1">{sn.title}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteSession(sn.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid="delete-session">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col bg-white rounded-3xl border border-linen-3 overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && !sending && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <span className="w-16 h-16 rounded-full bg-calm flex items-center justify-center mb-4 animate-breathe"><MessageCircleHeart className="w-8 h-8 text-brand" /></span>
              <h2 className="font-serif text-3xl text-ink">I'm here with you.</h2>
              <p className="text-ink-2 mt-2 max-w-sm">Share whatever is on your mind today — a feeling, a dream, a daily struggle. We'll look at it together.</p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
              <div className={`max-w-[80%] p-4 leading-relaxed text-[15px] whitespace-pre-wrap ${m.role === "user" ? "bg-linen-2 text-ink rounded-2xl rounded-tr-none" : "bg-calm text-calm-text rounded-2xl rounded-tl-none"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="typing">
              <div className="bg-calm text-calm-text rounded-2xl rounded-tl-none p-4 flex gap-1.5">
                <span className="w-2 h-2 bg-calm-text/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-calm-text/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-calm-text/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-linen-3 p-4">
          <div className="flex items-end gap-3 bg-linen rounded-3xl border border-linen-3 px-4 py-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Share what's on your heart…"
              className="border-0 bg-transparent resize-none min-h-[44px] max-h-32 focus-visible:ring-0 text-[15px]"
              data-testid="chat-input"
            />
            <Button onClick={send} disabled={sending || !input.trim()} size="icon" className="rounded-full bg-brand hover:bg-brand-hover text-linen shrink-0 mb-1" data-testid="chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Companion;
